import { defineStore } from "pinia"
import { useRecentEntitiesStore } from "@/store/recentEntities"

// A projects-shaped view over [[recentEntities]], which holds recently visited entities of *every*
// type. It stays as its own store because the dashboard card and the running-costs timeline read
// projects specifically, in `{ id, name, subtitle }` shape — this keeps that contract while the
// storage underneath is shared, so a project visit is recorded once and shows up in both the
// dashboard card and the command palette.

const TYPE = "PROJECT"

export const useRecentProjectsStore = defineStore("recentProjects", {
  getters: {
    getRecentProjects: () => () =>
      useRecentEntitiesStore()
        .getRecentEntities(TYPE)
        .map(entity => ({
          id: entity.id,
          name: entity.title,
          subtitle: entity.subtitle,
          visitedAt: entity.visitedAt,
        })),
  },
  actions: {
    /**
     * @param project `{ id, name, subtitle? }` — the shape the project detail view already passes.
     */
    addRecentProject(project) {
      if (!project || !project.id || !project.name) {
        console.warn("Invalid project data provided to addRecentProject")
        return
      }
      useRecentEntitiesStore().addRecentEntity({
        type: TYPE,
        id: project.id,
        title: project.name,
        subtitle: project.subtitle || "",
      })
    },
    /** Forgets one project, called when opening it was refused, so it stops being offered. */
    removeRecentProject(projectId) {
      if (!projectId) return
      useRecentEntitiesStore().removeRecentEntity({ type: TYPE, id: projectId })
    },
    /**
     * Clears **every** type, not just projects. Every caller is a session-identity change (logout,
     * 401, tenant switch), where leaving another type's entries behind would leak one tenant's
     * entity names into the next.
     */
    clearRecentProjects() {
      useRecentEntitiesStore().clearRecentEntities()
    },
  },
})
